import { useState, useEffect } from 'react';
import { Flex, Form, message, Spin } from 'antd';
import TicketFormBuilder from './TicketFormBuilder';
import { useCreateTicket, useFetchTicketFormFields } from '@/hooksApi';
import { useTranslation } from 'react-i18next';
import { Heading } from '@/components/Heading';
import { SingleTicketResponse, TicketField } from '@/types';
import { useTicketingStore } from '@/stores';
import { useQueryClient } from '@tanstack/react-query';
import {
  issueFieldList,
  otherIssueFieldByForm,
  TicketDescriptionField,
  TicketFormsEnum,
} from '@/constants';
import useLanguageStore from '@/stores/useLanguageStore';
import { getFormData } from '@/utils';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';

export default function TicketFormContainer() {
  const [isCreatingTicket, setIsSubmittingTicket] = useState<boolean>(false);
  const { language } = useLanguageStore();

  const { mutate: createTicket } = useCreateTicket();

  const { hasPermission } = usePermission();
  const { data: formFieldList, isLoading } = useFetchTicketFormFields(
    language,
    hasPermission(PermissionName.CREATE_TICKET),
  );

  const { ticketFormData, closeModal } = useTicketingStore();
  const queryClient = useQueryClient();

  const [form] = Form.useForm();
  const { t } = useTranslation();

  const currentForm = getFormData(
    ticketFormData?.ticket_form_id ?? TicketFormsEnum.APP,
    formFieldList?.object ?? [],
  );

  const isIssueField = (field?: TicketField) => {
    if (!field) return false;
    return issueFieldList.includes(field.id);
  };

  const handleSubmit = (_values: any) => {
    setIsSubmittingTicket(true);
    let currentIssueField: TicketField | undefined;

    const { attachments, description, ...values } = _values;
    const custom_fields: { id: number; value: string | null }[] = [];
    const normal_fields: { id: number; value: string | null; title: string | null }[] = [];

    Object.entries(values).forEach(([id, value]) => {
      const field = currentForm?.fields.find((f) => f.id === Number(id));

      if (isIssueField(field)) currentIssueField = field as TicketField;
      if (field?.custom) {
        custom_fields.push({
          id: Number(id),
          value: value ? String(value) : null,
        });
      } else {
        normal_fields.push({
          id: Number(id),
          value: value ? String(value) : null,
          title: field?.type ? String(field?.type) : null,
        });
      }
    });

    const selectedIssueValue: string | undefined = currentIssueField
      ? values[currentIssueField?.id]
      : undefined;

    console.log({ currentIssueField, selectedIssueValue });

    const selectedIssue = currentIssueField?.custom_field_options.find(
      (opt) => opt.value === selectedIssueValue,
    );
    if (currentIssueField && currentForm?.id) {
      const OTHERS_ID = otherIssueFieldByForm[currentForm.id as TicketFormsEnum];
      if (!selectedIssue) {
        // if issue is not selected, set other
        custom_fields.push({
          id: currentIssueField.id,
          value:
            currentIssueField.custom_field_options.find((option) => option.id === OTHERS_ID)
              ?.value ?? null,
        });
      } else if (selectedIssue.id === OTHERS_ID && !description) {
        message?.error(t('please_enter_description_for_other_option'));
        setIsSubmittingTicket(false);
        return;
      }
    }

    if (!selectedIssueValue && !description) {
      message?.error(t('please_enter_description_or_issue'));
      setIsSubmittingTicket(false);
      return;
    }

    const formData = new FormData();
    if (currentForm?.id) formData.append('type', currentForm.id.toString());
    formData.append('ticket_form_id', currentForm?.id?.toString() ?? '');

    (attachments as any[])?.forEach((file) => {
      if (file.originFileObj) {
        formData.append(`attachments[]`, file.originFileObj);
      }
    });

    custom_fields?.forEach((field, index) => {
      if (!!field.value && !!field.id) {
        //skip null values
        formData.append(`custom_fields[${index}][id]`, field.id.toString());
        formData.append(`custom_fields[${index}][value]`, field.value.toString());
      }
    });

    normal_fields?.forEach((field, index) => {
      if (field.value) {
        formData.append(`fields[${index}][title]`, field.title!.toString());
        formData.append(`fields[${index}][value]`, field.value ?? '');
      }
    });

    // Append description field
    if (description || selectedIssueValue) {
      const descIndex = normal_fields.length;
      formData.append(`fields[${descIndex}][title]`, 'description');
      formData.append(`fields[${descIndex}][value]`, description ?? selectedIssue?.name ?? '');
    }

    createTicket(formData, {
      onSuccess: () => {
        message?.success(t('operation_completed_successfully'));
        queryClient.invalidateQueries(['tickets']);
        setIsSubmittingTicket(false);
        handleCloseModal();
      },
      onError: () => {
        setIsSubmittingTicket(false);
      },
    });
  };

  const transferToFormValue = (ticketData: SingleTicketResponse) => {
    if (!ticketData) return {};

    const formValues: Record<string, any> = {};

    formValues['subject'] = ticketData.subject;
    formValues['description'] = ticketData.description;

    // custom_fields
    ticketData.custom_fields?.forEach((field) => {
      formValues[String(field.id)] = field.value;
    });

    // Set uid field value from entity_uid
    if (ticketData.entity_uid && currentForm?.fields) {
      const uidField = currentForm.fields.find((field) => field.key === 'uid');
      if (uidField) {
        formValues[String(uidField.id)] = ticketData.entity_uid;
      }
    }

    return formValues;
  };

  const handleCloseModal = () => {
    form.resetFields();
    closeModal();
  };

  useEffect(() => {
    if (formFieldList) {
      form.setFieldsValue(ticketFormData ? transferToFormValue(ticketFormData) : undefined);
    }
  }, [ticketFormData, formFieldList]);

  const isFromEntityDetailPage =
    !!ticketFormData && ticketFormData.custom_fields?.length === 1 && !ticketFormData.id; // if is creating from order/remittance/.. pages

  return (
    <div className='mt-2'>
      <Form
        initialValues={ticketFormData ? transferToFormValue(ticketFormData) : undefined}
        layout='vertical'
        form={form}
        onFinish={handleSubmit}
        style={{ margin: '0 auto' }}
      >
        {isLoading ? (
          <Spin />
        ) : (
          <TicketFormBuilder disableEntityId={isFromEntityDetailPage} ticketForm={currentForm} />
        )}
        <Flex gap='16px' justify='end' className='mt-4'>
          <button
            type='button'
            onClick={handleCloseModal}
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors uppercase'
          >
            {t('cancel')}
          </button>
          <Form.Item className='m-0'>
            <button
              type='submit'
              className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors uppercase'
              disabled={isCreatingTicket}
            >
              {isCreatingTicket && (
                <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
              )}
              {t('submit_create_ticket')}
            </button>
          </Form.Item>
        </Flex>
      </Form>
    </div>
  );
}
